import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import {
	Sheet,
	SheetBackdrop,
	SheetBody,
	SheetBodyPadding,
	SheetFooter,
	SheetHeader
} from '@inter-ikea-kompis/component-sheet';
import { Text } from '@inter-ikea-kompis/component-text';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';

import CountdownBannerAnimationNameEnum from '../../enums/CountdownBannerAnimationNameEnum.js';
import type ICountdownBannerLocalizedInformation from '../../types/ICountdownBannerLocalizedInformation.js';
import type ICountdownBannerTranslations from '../../types/ICountdownBannerTranslations.js';
import Countdown from '../countdown/Countdown.js';
import CountdownBannerStyle from './CountdownBannerStyle.js';

/**
 * Banner if user leave the screen idle for some time.
 *
 * @event close Triggered by user.
 * @event sessionClick When user clicks start new session.
 * @event timeEnd When the animation for circle tick ends.
 * @event timeChange When duration change.
 * @event animationEnd When the animation ends.
 */

export default class CountdownBanner extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: ICountdownBannerLocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: ICountdownBannerTranslations | null = null;

	// Start value for countdown.
	@property({ type: Number })
	public startTime = 0;

	// Current value for countdown.
	@property({ type: Number })
	public time = 0;

	// Show or hide banner.
	@property({ type: Boolean })
	public visible = false;

	// Show or hide secondary button.
	@property({ type: Boolean })
	public showSecondaryButton = false;

	// Checks for animation open/close.
	private isAnimating = false;

	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			return null;
		}

		if (!this.visible && !this.isAnimating) {
			return null;
		}

		return html`
			<style>
				${CountdownBannerStyle.getStyle({
					theme: this.theme,
					isVisible: this.visible,
					isAnimating: this.isAnimating
				})}
			</style>

			<div class="skapa-focus-portal">${this.getBackdrop()} ${this.getContainer()}</div>
		`;
	}

	public override async connectedCallback(): Promise<void> {
		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('kompis-text', Text);
		this.customElements.define('kompis-sheet', Sheet);
		this.customElements.define('kompis-sheet-header', SheetHeader);
		this.customElements.define('kompis-sheet-body', SheetBody);
		this.customElements.define('kompis-sheet-footer', SheetFooter);
		this.customElements.define('kompis-sheet-body-padding', SheetBodyPadding);
		this.customElements.define('kompis-sheet-backdrop', SheetBackdrop);
		this.customElements.define('kompis-countdown', Countdown);

		super.connectedCallback();
	}

	/**
	 * @param changedProperties Changed properties.
	 */
	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (
			typeof changedProperties.get('visible') === 'boolean' &&
			typeof this.visible === 'boolean'
		) {
			this.isAnimating = true;
		}
		return true;
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation?.translations;
	}

	private getTranslations(): ICountdownBannerTranslations {
		const translations = this.translations
			? this.translations
			: (this.localizedInformation?.translations as ICountdownBannerTranslations);

		return translations;
	}
	/**
	 * @returns Template.
	 */
	private getBackdrop(): RenderResult {
		return html`
			<kompis-sheet-backdrop
				@click="${() => this.dispatchEvent(new CustomEvent('close', { bubbles: true }))}"
				.visible="${this.visible}"
				.theme="${this.theme}"
			></kompis-sheet-backdrop>
		`;
	}

	/**
	 * @returns - Html.
	 */
	private getContainer(): RenderResult {
		const { countdownBannerHeader, buttonContinue } = this.getTranslations();
		const primaryVariant: ButtonVariant = 'primary';

		return html`
			<div class="container" @animationend="${this.onAnimationEnd.bind(this)}">
				<div class="bannerContainer">
					<div class="leftContent">
						<kompis-text
							class="countdownBannerHeader"
							.theme="${this.theme}"
							.type="${ThemeFontStyleTypeEnum.headingMedium}"
						>
							${countdownBannerHeader}
						</kompis-text>
					</div>
					<div class="rightContent">
						<kompis-countdown
							@timeEnd="${() => this.dispatchEvent(new CustomEvent('timeEnd', { bubbles: true }))}"
							@timeChange="${(event: CustomEvent) =>
								this.dispatchEvent(
									new CustomEvent('timeChange', { bubbles: true, detail: event.detail })
								)}"
							.theme="${this.theme}"
							.startTime="${this.startTime}"
							.time="${this.time}"
						>
						</kompis-countdown>
						${this.getSessionButton()}
						<skapa-button
							@click="${() => this.dispatchEvent(new CustomEvent('close', { bubbles: true }))}"
							class="buttonContinue"
							variant="${primaryVariant}"
						>
							${buttonContinue}
						</skapa-button>
					</div>
				</div>
			</div>
		`;
	}

	/**
	 * Returns session button if not mobile.
	 */
	private getSessionButton(): RenderResult {
		if (!this.showSecondaryButton) {
			return null;
		}

		const countdownBannerGalleryButton = this.getTranslations().countdownBannerGalleryButton;
		const secondaryVariant: ButtonVariant = 'secondary';

		return html`
			<skapa-button
				@click="${() => this.dispatchEvent(new CustomEvent('sessionClick', { bubbles: true }))}"
				class="countdownBannerGalleryButton"
				variant="${secondaryVariant}"
			>
				${countdownBannerGalleryButton}
			</skapa-button>
		`;
	}

	/**
	 * Listener for animationend.
	 *
	 * @param event Html event.
	 */
	private onAnimationEnd(event: AnimationEvent): void {
		this.isAnimating = false;
		if (event.animationName.startsWith(CountdownBannerAnimationNameEnum.closing)) {
			this.dispatchEvent(
				new CustomEvent('animationEnd', {
					bubbles: true,
					detail: {
						type: 'close'
					}
				})
			);
		} else if (event.animationName.startsWith(CountdownBannerAnimationNameEnum.opening)) {
			this.dispatchEvent(
				new CustomEvent('animationEnd', {
					bubbles: true,
					detail: {
						type: 'open'
					}
				})
			);
		}
		this.update();
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'kompis-countdown-banner': CountdownBanner;
	}
}
